import { ApolloError } from '@apollo/client';
import { type ErrorResponse, onError } from '@apollo/client/link/error';
import type { ErrorKind } from '@shared/errors';
import type { GraphQLFormattedError } from 'graphql';

import { notify, toUserMessage } from 'src/lib/notify';

// ---------------------------------------------------------------------------
// Apollo Link — surface typed AppError GraphQL extensions as notifications
// ---------------------------------------------------------------------------

/**
 * Centralized error link (TAS-3247). When the backend `APP_ERROR` flag is ON, the Yoga `errorPlugin` stamps every GraphQL error with taxonomy `extensions`; this link reads that contract and surfaces the error exactly once via `notify.*`.
 * ADDITIVE + INERT while the flag is OFF — with no `extensions.code`, `surfaceAppError` no-ops and existing per-mutation `onError` call sites keep working.
 * `networkError` is toasted only when `APP_ERROR` is on, and THROTTLED to one toast per window.
 * Composed ABOVE the 401 link, so it observes the FINAL post-retry response — a transient 401 that recovers never toasts.
 */

/** kinds whose message is a user warning; anything else is a hard error toast. */
const WARN_KINDS: ReadonlySet<string> = new Set<ErrorKind>(['validation', 'not_found', 'conflict', 'auth']);

/** Taxonomy fields this link reads off `extensions`. `kind` stays `string` — untrusted wire data validated only for choosing the notify level. `extensions.field` is deliberately not read until inline routing lands. */
interface AppErrorExtensions {
  code: string;
  kind: string;
  suggestion?: string;
  referenceSlug?: string;
}

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Reads our taxonomy off `extensions`, or `null` when the error is not ours
 *  (no `code` → flag OFF or a non-taxonomy error → this link stays inert). */
function parseAppErrorExtensions(extensions: GraphQLFormattedError['extensions']): AppErrorExtensions | null {
  if (!extensions) return null;
  const code = asNonEmptyString(extensions.code);
  if (!code) return null;

  return {
    code,
    kind: asNonEmptyString(extensions.kind) ?? '',
    suggestion: asNonEmptyString(extensions.suggestion),
    referenceSlug: asNonEmptyString(extensions.referenceSlug),
  };
}

/** Unknown/system/integration kinds → `error`; user-tier kinds → `warn`. An
 *  unrecognized kind defaults to `error` so a mis-tagged failure is never
 *  silently downgraded. */
function levelForKind(kind: string): 'error' | 'warn' {
  return WARN_KINDS.has(kind) ? 'warn' : 'error';
}

/** message + suggestion + reference code (whichever the backend provided). The
 *  backend gates `referenceSlug` visibility via `isReferenceVisible`, so the FE
 *  simply surfaces it whenever present. */
function composeMessage(message: string, extensions: AppErrorExtensions): string {
  const parts = [message];
  if (extensions.suggestion) parts.push(extensions.suggestion);
  if (extensions.referenceSlug) parts.push(`Reference: ${extensions.referenceSlug}`);
  return parts.join(' ');
}

/** Surfaces a single GraphQL error via `notify.*` when it carries our taxonomy; returns `true` if it notified. Field-scoped errors (`extensions.field`) fall back to a toast until inline routing is built. */
export function surfaceAppError(error: GraphQLFormattedError): boolean {
  const extensions = parseAppErrorExtensions(error.extensions);
  if (!extensions) return false;

  const message = composeMessage(error.message, extensions);
  if (levelForKind(extensions.kind) === 'error') notify.error(message);
  else notify.warn(message);
  return true;
}

/** True when this link already toasted the failure (an `ApolloError` carrying a typed AppError), so a caller's own
 *  catch has nothing to add. Untyped GraphQL errors and network errors return `false`. */
export function wasSurfacedByAppErrorLink(error: unknown): boolean {
  return (
    error instanceof ApolloError &&
    error.graphQLErrors.some((graphQLError) => parseAppErrorExtensions(graphQLError.extensions) !== null)
  );
}

/** A caller's own failure path: toast `failure` plus the error's user-safe message, unless this link already surfaced
 *  it — a typed AppError, or a network failure while `APP_ERROR` is on — so the user never sees a failure twice or not at all. */
export function notifyUnlessSurfaced(error: unknown, failure: string): void {
  if (wasSurfacedByAppErrorLink(error)) return;
  if (error instanceof ApolloError && error.networkError && process.env.APP_ERROR === 'true') return;
  notify.error(`${failure} ${toUserMessage(error)}`);
}

/** Shown for a transport failure that carries no GraphQL body (500 / timeout /
 *  offline). Deliberately generic so no internal detail leaks. */
const NETWORK_ERROR_MESSAGE = 'Could not reach the server. Check your connection and try again.';
/** A genuinely dead session surfaces here as a 401 — the 401 replay link only retries, it never resolves a dead session — so it gets a sign-in message rather than a misleading "check your connection". */
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

/** Offline / server-down failures arrive once PER failing operation (background
 *  refetches included), so one offline moment would fire a storm of identical
 *  toasts. Show at most one network toast per this window. */
const NETWORK_TOAST_THROTTLE_MS = 5_000;
let lastNetworkToastAt = 0;

function networkErrorMessage(networkError: NonNullable<ErrorResponse['networkError']>): string {
  if ('statusCode' in networkError && networkError.statusCode === 401) return SESSION_EXPIRED_MESSAGE;
  return NETWORK_ERROR_MESSAGE;
}

/** The subset of Apollo's `ErrorResponse` this callback reads. */
type ApolloErrorInput = Pick<ErrorResponse, 'graphQLErrors' | 'networkError'>;

/** Core of the Apollo `onError` callback. GraphQL errors surface via the taxonomy (self-gating: no `extensions.code` ⇒ inert). A bare `networkError` carries no taxonomy, so it is toasted only behind `APP_ERROR` and throttled so an offline burst yields one toast. */
export function surfaceApolloError({ graphQLErrors, networkError }: ApolloErrorInput): void {
  if (graphQLErrors?.length) {
    for (const error of graphQLErrors) surfaceAppError(error);
    return;
  }
  if (!networkError || process.env.APP_ERROR !== 'true') return;

  const now = Date.now();
  if (now - lastNetworkToastAt < NETWORK_TOAST_THROTTLE_MS) return;
  lastNetworkToastAt = now;
  notify.error(networkErrorMessage(networkError));
}

export const appErrorLink = onError(surfaceApolloError);
